import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
	SEND_TEMPLATE_MESSAGE,
	GET_CONTACT_IDS_FOR_SEND,
	CREATE_NEW_CONTACTS,
	GET_CONVERSATIONS_IDS,
	GET_LATEST_INBOUND_MESSAGES,
	SEND_NOT_DEFERRED_MESSAGE,
	GET_RESELLER_NAME,
	CHECK_MIME_TYPE,
	GET_PRESUB_FROM_IDS,
} from "./query";
import {
	ADD_MESSAGE_TO_CONVERSATION_DEFERRED,
	GET_TEMPLATE_MESSAGE_BY_NAME,
} from "../new-natural-chat/query";
import { Formik } from "formik";
import { useLazyQuery, useMutation, useReactiveVar } from "@apollo/client";
import Stepper from "../Stepper/stepper.component";
import Step1 from "./send-template-message-step1.component";
import Step2 from "./send-template-message-step2.component";
import client from "../../Apollo";
import { useToasts } from "react-toast-notifications";
import { getAccessToken } from "../../helpers/functions";
import Modal from "react-modal";
import {
	ModalDescription,
	ModalHeader,
	ModalContentSeperator,
} from "../MsgBoxModal/styles";
import ReactGA from "react-ga4";
import useCurrentUserDetails from "../../helpers/use-current-user-details";
import { generateValidationScheme } from "./valiadtaion-schema";
import { $isMessageSending, $selectedConversation } from "../../store";
import Tags from "../TagsFormField/tags.component";
import MessageSendingProgressModal from "../MessageSendingProgressModal/MessageSendingProgressModal";
import {
	NATURAL_CHAT_TEMPLATE_TEXT_NAME,
	NATURAL_CHAT_TEMPLATE_MEDIA_NAME,
} from "../new-natural-chat/constants";
import ConvertingModal from "../ConvertingModal/convertingModal";
import {
	buildFullDynamicUrl,
	formatButtonParamsForMutation,
	getDynamicUrlButtonsForSend,
} from "../../helpers/dynamic-url-cta-buttons";

const SendTemplateMessage = ({
	rootStore,
	isOpen,
	close,
	disableSearchBy,
	contacts,
	disabledFields,
	account,
	table,
	showPendingMessage = false,
}) => {
	const { currentUser } = useCurrentUserDetails();
	const [addContacts] = useMutation(CREATE_NEW_CONTACTS);
	const [sendMessage, { error }] = useMutation(SEND_TEMPLATE_MESSAGE);
	const [addMessageMutation, { error: messageMutationError, loading }] =
		useMutation(ADD_MESSAGE_TO_CONVERSATION_DEFERRED);

	const [checkMimeType] = useLazyQuery(CHECK_MIME_TYPE);

	const [sendNotDeferredMessage] = useMutation(SEND_NOT_DEFERRED_MESSAGE);
	const [naturalChatTemplate, setNaturalChatTemplate] = useState(null);
	const [newNaturalChatTemplate, setNewNaturalChatTemplate] = useState(null);
	const [submitClicked, setSubmitClicked] = useState(false);
	const [maxBroadcasts, setMaxBroadcasts] = useState(0);
	const [preSubAsSub, setPreSubAsSub] = useState(false);
	const [preSubCount, setPreSubCount] = useState(0);
	const [preSubList, setPreSubList] = useState([]);
	const [showSendingProgressModal, setShowSendingProgressModal] =
		useState(false);
	const [tagsContactsCount, setTagsContactsCount] = useState(0);
	const [recipientState, setRecipientsState] = useState();
	const [converting, setConverting] = useState(false);
	const [converted, setIsConverted] = useState();
	const TIMEOUT_DURATION = 1200000;
	const selectedConversation = useReactiveVar($selectedConversation);
	const [selectedInbox, setSelectedInbox] = useState(null);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	const orgId = localStorage.getItem("org_id");
	const [resellerName, setResellerName] = useState("");
	const [getResellerName] = useLazyQuery(GET_RESELLER_NAME);

	const [currentStep, setCurrentStep] = useState(1);
	const [isSent, setIsSent] = useState(false);
	const { addToast } = useToasts();
	const [disableSubmit, setDisableSubmit] = useState(false);

	const [advancedSaved, setAdvancedSaved] = useState(false);
	const [advancedSendContacts, setAdvancedSendContacts] = useState([]);

	const [batchState, setBatchState] = useState({
		isSending: false,
		currentBatch: 0,
		totalBatches: 0,
		error: null,
	});

	const BATCH_SIZE = 50;

	useEffect(() => {
		fetchResellerName();
	}, [orgId]);

	const fetchResellerName = async () => {
		let result = await getResellerName({
			variables: {
				orgId: orgId,
			},
		});
		if (
			result &&
			result.data &&
			result.data.msgbox_Organisation &&
			result.data.msgbox_Organisation[0].Reseller &&
			result.data.msgbox_Organisation[0].Reseller.Name
		) {
			setResellerName(result.data.msgbox_Organisation[0].Reseller.Name);
		} else {
			setResellerName("");
		}
	};

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const isUsableConversationId = (id) => {
		const n = Number(id);
		return id != null && id !== "" && id !== -1 && Number.isFinite(n) && n > 0;
	};

	// client.query + network-only — useLazyQuery(GET_CONVERSATION_BY_CONTACT)
	// in Apollo 3.3 often resolves with empty/stale data even when the
	// contact already has a conversation. GET_CONVERSATIONS_IDS is the same
	// lookup the text-only path already uses successfully.
	const lookupConversationsByContact = async (contactIds) => {
		if (!contactIds.length) return [];
		const { data } = await client.query({
			query: GET_CONVERSATIONS_IDS,
			variables: { contactIds },
			fetchPolicy: "network-only",
		});
		return data?.msgbox_Conversation ?? [];
	};

	const resolveConversationsForRecipients = async (recipients) => {
		const contactIds = recipients
			.map((recipient) => Number(recipient.ContactId))
			.filter((id) => Number.isFinite(id) && id > 0);
		const selectedId = isUsableConversationId(selectedConversation)
			? Number(selectedConversation)
			: null;

		// Opened from an existing conversation: use that id directly,
		// matching new-natural-chat.js. Do not depend on a contact lookup.
		if (selectedId && contactIds.length === 1) {
			return [{ ConversationId: selectedId, ContactId: contactIds[0] }];
		}

		let rows = await lookupConversationsByContact(contactIds);
		if (rows.length === 0 && contactIds.length > 0) {
			for (let attempt = 0; attempt < 3 && rows.length === 0; attempt++) {
				await delay(500);
				rows = await lookupConversationsByContact(contactIds);
			}
		}

		return rows;
	};

	const sendTemplateInBatches = async ({
		templateId,
		message,
		accountId,
		recipients,
		attachmentId = null,
		followOnMessage,
		buttonParams = null,
	}) => {
		if (!recipients || recipients.length === 0) return;

		// For small sends keep existing UX and a single mutation.
		if (recipients.length <= 10) {
			await sendMessage({
				variables: {
					attachmentId,
					TemplateId: templateId,
					Message: message,
					account: accountId,
					contacts: {
						data: recipients,
					},
					...(followOnMessage ? { followOnMessage } : {}),
					...(buttonParams ? { buttonParams } : {}),
				},
			});
			return;
		}

		const totalBatches = Math.ceil(recipients.length / BATCH_SIZE);

		setBatchState({
			isSending: true,
			currentBatch: 0,
			totalBatches,
			error: null,
		});

		try {
			for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
				const start = batchIndex * BATCH_SIZE;
				const end = start + BATCH_SIZE;
				const batchRecipients = recipients.slice(start, end);

				// 2s delay before each batch
				await delay(2000);

				await sendMessage({
					variables: {
						attachmentId,
						TemplateId: templateId,
						Message: message,
						account: accountId,
						contacts: {
							data: batchRecipients,
						},
						...(followOnMessage ? { followOnMessage } : {}),
						...(buttonParams ? { buttonParams } : {}),
					},
				});

				// 2s delay after each batch
				await delay(2000);

				setBatchState((prev) => ({
					...prev,
					currentBatch: batchIndex + 1,
				}));
			}
		} catch (e) {
			const failedBatch =
				batchState.currentBatch + 1 > totalBatches
					? totalBatches
					: batchState.currentBatch + 1;
			const errorMessage = `Sending failed during batch ${failedBatch} of ${totalBatches}. Some contacts may already have received the message.`;

			setBatchState((prev) => ({
				...prev,
				error: errorMessage,
			}));

			throw e;
		} finally {
			setBatchState((prev) => ({
				...prev,
				isSending: false,
			}));
		}
	};

	// natural_chat_media is text-only. Send the template without a media
	// header, then hold the file as a deferred (paused) message per
	// conversation — matching new-natural-chat sendNewNaturalChatTemplate.
	const sendNaturalChatMediaAndDeferAttachment = async ({
		templateId,
		templateMessage,
		accountId,
		recipients,
		attachmentId,
	}) => {
		await sendTemplateInBatches({
			templateId,
			message: templateMessage,
			accountId,
			recipients: recipients.map((x) => ({
				ContactId: x.ContactId,
			})),
		});

		const conversationRows = await resolveConversationsForRecipients(
			recipients
		);

		if (!conversationRows.length) {
			addToast(
				"Template sent, but the attachment could not be queued as a pending message.",
				{
					appearance: "error",
					autoDismiss: true,
				}
			);
			return false;
		}

		await Promise.all(
			conversationRows.map((conversation) =>
				addMessageMutation({
					variables: {
						bodyText: "",
						conversationId: Number(conversation.ConversationId),
						attachmentId,
					},
				})
			)
		);

		const queuedOnSelected =
			isUsableConversationId(selectedConversation) &&
			conversationRows.some(
				(row) =>
					Number(row.ConversationId) === Number(selectedConversation)
			);
		if (queuedOnSelected || showPendingMessage) {
			$isMessageSending(true);
		}

		return true;
	};

	async function checkAttachment(
		attachmentId,
		recipients,
		templateId,
		templateMessage,
		accountId,
		isVideoTemplate,
		buttonParams = null
	) {
		setConverting(true);
		const startTime = Date.now();
		async function retryQuery() {
			if (Date.now() - startTime >= TIMEOUT_DURATION) {
				setConverting(false);
				console.warn("Request timed out.");
				return;
			}
			const data = await checkMimeType({
				variables: { attachmentId },
				fetchPolicy: "no-cache",
			});
			//conversion complete on back end
			if (data.data.msgbox_Attachment[0].MimeType === "video/mp4") {
				//freeform/nat chat video message
				if (isVideoTemplate === false) {
					const queued = await sendNaturalChatMediaAndDeferAttachment({
						templateId,
						templateMessage,
						accountId,
						recipients,
						attachmentId,
					});
					setIsConverted(true);
					setConverting(false);
					if (queued) {
						addToast("Messages sent successfully", {
							appearance: "success",
							autoDismiss: true,
						});
					}
				}
				//specific video template message
				else {
					await sendTemplateInBatches({
						templateId,
						message: templateMessage,
						accountId,
						recipients,
						attachmentId,
						buttonParams,
					});
					setIsConverted(true);
					setConverting(false);
					addToast("Messages sent successfully", {
						appearance: "success",
						autoDismiss: true,
					});
				}
			} else {
				console.log("retrying.. ");
				setTimeout(retryQuery, 1000); // Retry after 1 second
			}
		}
		retryQuery();
	}

	const sendOwnMessage = async (values, actions, recipients) => {
		try {
			if (values?.attachments?.length > 0) {
				let attachmentId = await uploadAttachment(
					values.attachments[0]
				);
				if (!attachmentId) return;
				else {
					let templateName = NATURAL_CHAT_TEMPLATE_MEDIA_NAME;

					const getNaturalChatMediaTemplate = await client.query({
						query: GET_TEMPLATE_MESSAGE_BY_NAME,
						variables: {
							name: templateName,
							userInboxes: [values.account.inboxId],
						},
					});

					const templateText =
						getNaturalChatMediaTemplate.data.msgbox_Template[0]
							.TemplateText;
					const templateMessage = templateText.replace(
						"{{1}}",
						values.message
					);
					const templateId =
						getNaturalChatMediaTemplate.data.msgbox_Template[0]
							.TemplateId;
					const accountId = values.account.value;

					//if non mp4 video, convert
					if (values?.attachments[0]?.type?.includes("video")) {
						//checks *and* sends
						checkAttachment(
							attachmentId,
							recipients,
							templateId,
							templateMessage,
							accountId,
							false
						);
					} else {
						//Just send, no need to check attachment mime type
						const queued = await sendNaturalChatMediaAndDeferAttachment({
							templateId,
							templateMessage,
							accountId,
							recipients,
							attachmentId,
						});
						if (queued) {
							addToast("Messages sent successfully", {
								appearance: "success",
								autoDismiss: true,
							});
						}
					}
				}
			} else if (newNaturalChatTemplate) {
				const templateText = newNaturalChatTemplate.TemplateText;
				const templateMessage = templateText.replace(
					"{{1}}",
					values.message
				);

				await sendTemplateInBatches({
					templateId: newNaturalChatTemplate.TemplateId,
					message: templateMessage,
					accountId: disabledFields.account
						? account
						: values.account.value,
					recipients: recipients.map((x) => ({
						ContactId: x.ContactId,
					})),
				});
				addToast("Messages sent successfully", {
					appearance: "success",
					autoDismiss: true,
				});
			} else {
				const templateText = naturalChatTemplate.TemplateText;

				const msg = templateText.replace(
					"{{1}}",
					currentUser.FirstName
				);
				// get conversations for contacts
				const { data: conversationIdsData } = await client.query({
					query: GET_CONVERSATIONS_IDS,
					variables: {
						contactIds: recipients.map((rec) => rec.ContactId),
					},
				});

				const contactsWithoutConversations = recipients.filter(
					(recipient) =>
						!conversationIdsData.msgbox_Conversation
							.map((x) => x.ContactId)
							.includes(recipient.ContactId)
				);
				if (contactsWithoutConversations.length > 0) {
					await sendTemplateInBatches({
						templateId: naturalChatTemplate.TemplateId,
						message: msg,
						accountId: disabledFields.account
							? account
							: values.account.value,
						recipients: contactsWithoutConversations,
						followOnMessage: values.message,
					});
				}
				if (conversationIdsData.msgbox_Conversation.length > 0) {
					let tempDate = new Date();
					tempDate.setDate(tempDate.getDate() - 1);
					const { data: getLatestInboundMessage } =
						await client.query({
							query: GET_LATEST_INBOUND_MESSAGES,
							variables: {
								ids: conversationIdsData.msgbox_Conversation.map(
									(x) => x.ConversationId
								),
								_gt: tempDate,
							},
						});

					// conversations inside 24h window
					if (getLatestInboundMessage.msgbox_Message.length > 0) {
						const promises =
							getLatestInboundMessage.msgbox_Message.map((x) => {
								sendNotDeferredMessage({
									variables: {
										Message: values.message,
										conversationId: x.ConversationId,
									},
								});
							});
						await Promise.all(promises);
					}

					const conversationsOutside24h =
						conversationIdsData.msgbox_Conversation.filter(
							(convData) =>
								!getLatestInboundMessage.msgbox_Message
									.map((x) => x.ConversationId)
									.includes(convData.ConversationId)
						);
					if (conversationsOutside24h.length > 0) {
						await sendTemplateInBatches({
							templateId: naturalChatTemplate.TemplateId,
							message: msg,
							accountId: disabledFields.account
								? account
								: values.account.value,
							recipients: conversationsOutside24h.map((x) => ({
								ContactId: x.ContactId,
							})),
							followOnMessage: values.message,
						});

						addToast("Messages sent successfully", {
							appearance: "success",
							autoDismiss: true,
						});
					}
				}
			}

			actions.resetForm();
			close();

			if (table) {
				table.current.refetchData();
			}
			return;
		} catch (e) {
			console.log("error", e);
			if (batchState.error) {
				addToast("Partial send; check Sent Template Messages for details", {
					appearance: "error",
					autoDismiss: true,
				});
			} else {
				addToast("Error sending messages", {
					appearance: "error",
					autoDismiss: true,
				});
			}
		}
	};

	const handleCloseModal = () => {
		setShowSendingProgressModal(false);
	};

	const onSubmit = async (values, actions) => {
		setDisableSubmit(true);
		setRecipientsState(values.contacts);

		setIsSent(false);

		let addedContactsArray = [];
		const contactsToBeAdded = values.contacts.filter(
			(x) => x.status === "NEW_CONTACT"
		);
		if (contactsToBeAdded.length > 0) {
			const { data: addedContacts } = await addContacts({
				variables: {
					objects: contactsToBeAdded.map((x) => {
						return {
							Name: x.value.trim().replace(" ", ""),
							MobileNumber: x.value.trim().replace(" ", ""),
							Status: "Presubscribed",
							APIAccountId: values.account.value,
						};
					}),
				},
			});

			addedContactsArray =
				addedContacts.insert_msgbox_Contact.returning.map((x) => {
					return { ContactId: x.ContactId };
				});
		}
		const alreadyExistingContacts = values.contacts.filter(
			(x) => x.status !== "NEW_CONTACT"
		);
		let recipients = [];
		let attachmentId = null;
		// using form with ability to select contacts
		if (contacts.length <= 0) {
			if (values.searchBy === "search-contacts") {
				// already existing and new contacts here
				recipients = alreadyExistingContacts.map((contact) => ({
					ContactId: contact.value,
				}));
				if (addedContactsArray.length > 0) {
					recipients = [...recipients, ...addedContactsArray];
				}
			} else {
				// Fetch all contacts that have any of the selected tags.
				if (advancedSaved === false) {
					const tags = values.tags.map((tag) =>
						tag.label.replace(/ *\([^)]*\) */g, "")
					);
					// Page through the audience so large tag selections (600+)
					// don't hit Hasura's 60s statement timeout in a single query.
					const PAGE_SIZE = 500;
					const taggedContacts = [];
					let offset = 0;
					// eslint-disable-next-line no-constant-condition
					while (true) {
						const page = await client.query({
							query: GET_CONTACT_IDS_FOR_SEND,
							variables: {
								tags,
								account: values.account.value,
								limit: PAGE_SIZE,
								offset,
							},
						});
						const rows = page.data.msgbox_Contact;
						taggedContacts.push(...rows);
						if (rows.length < PAGE_SIZE) break;
						offset += PAGE_SIZE;
					}
					if (preSubAsSub === false) {
						const preSubSet = new Set(
							preSubList.map((contact) => contact.contactId)
						);

						const subscribedContacts = [];
						const prioritizedPreSubs = [];
						const otherPreSubs = [];

						for (const contact of taggedContacts) {
							if (contact.Status === "Subscribed") {
								subscribedContacts.push({
									ContactId: contact.ContactId,
								});
							} else if (contact.Status === "Presubscribed") {
								if (preSubSet.has(contact.ContactId)) {
									prioritizedPreSubs.push({
										ContactId: contact.ContactId,
									});
								} else {
									otherPreSubs.push({
										ContactId: contact.ContactId,
									});
								}
							}
						}

						let finalPreSubs = [];
						if (prioritizedPreSubs.length > 10) {
							finalPreSubs = prioritizedPreSubs.slice(0, 10);
						} else {
							finalPreSubs = [
								...prioritizedPreSubs,
								...otherPreSubs.slice(
									0,
									10 - prioritizedPreSubs.length
								),
							];
						}

						recipients = [...subscribedContacts, ...finalPreSubs];
					} else {
						recipients = taggedContacts.map((contact) => {
							return {
								ContactId: contact.ContactId,
							};
						});
					}
				}
				//else use advanced send contact list / criteria
				else if (advancedSaved === true) {
					if (preSubAsSub === false) {
						const preSubSet = new Set(
							preSubList.map((contact) => contact.contactId)
						);

						const subscribedContacts = [];
						const prioritizedPreSubs = [];
						const otherPreSubs = [];

						for (const contact of advancedSendContacts) {
							if (contact.Status === "Subscribed") {
								subscribedContacts.push({
									ContactId: contact.ContactId,
								});
							} else if (contact.Status === "Presubscribed") {
								if (preSubSet.has(contact.ContactId)) {
									prioritizedPreSubs.push({
										ContactId: contact.ContactId,
									});
								} else {
									otherPreSubs.push({
										ContactId: contact.ContactId,
									});
								}
							}
						}

						let finalPreSubs = [];
						if (prioritizedPreSubs.length > 10) {
							finalPreSubs = prioritizedPreSubs.slice(0, 10);
						} else {
							finalPreSubs = [
								...prioritizedPreSubs,
								...otherPreSubs.slice(
									0,
									10 - prioritizedPreSubs.length
								),
							];
						}
						recipients = [...subscribedContacts, ...finalPreSubs];
					} else {
						recipients = advancedSendContacts.map((contact) => {
							return { ContactId: contact.ContactId };
						});
					}
					setAdvancedSaved(false);
				}
			}
		} else {
			recipients = contacts.map((contact) => ({
				ContactId: contact.value,
			}));
		}
		//finalised list of recipients, if needed to test w/out sending:
		//console.log(recipients, " < recip");

		//sending logic:
		if (values.message && values.message.length > 0) {
			if (
				!/\n/.test(values.message) &&
				(naturalChatTemplate || newNaturalChatTemplate)
			) {
				await sendOwnMessage(values, actions, recipients);
			}
			setDisableSubmit(false);
			return;
		}
		if (values?.attachments?.length > 0) {
			attachmentId = await uploadAttachment(values.attachments[0]);
			if (!attachmentId) return;
		}
		let message = values.templateMessages.value.text;
		if (values.variables.length > 0) {
			message = values.templateMessages.value.text;
			values.variables.forEach((variable) => {
				if (
					variable.value === "contactname" ||
					variable.value === "saluteas" ||
					variable.value === "firstname" ||
					variable.value === "lastname" ||
					variable.value === "orgname" ||
					variable.value === "inboxname" ||
					variable.value === "fullname"
				) {
					message = message.replace(
						`{{${variable.label}}}`,
						`{{${variable.value}}}`
					);
				} else {
					message = message.replace(
						`{{${variable.label}}}`,
						variable.value
					);
				}
			});
			setSubmitClicked(false);
		}
		try {
			const buttonParams = formatButtonParamsForMutation(
				values.buttonUrlParams
			);
			if (values?.attachments[0]?.type?.includes("video")) {
				let acc = disabledFields.account ? account : values.account.value;
				await checkAttachment(
					attachmentId,
					recipients,
					values.templateMessages.value.templateId,
					message,
					acc,
					true,
					buttonParams
				);
			} else {
				await sendTemplateInBatches({
					templateId: values.templateMessages.value.templateId,
					message,
					accountId: disabledFields.account ? account : values.account.value,
					recipients,
					attachmentId,
					buttonParams,
				});
				addToast("Templates sent successfully", {
					appearance: "success",
					autoDismiss: true,
				});
			}

			setAdvancedSaved(false);
			actions.resetForm();
			setIsSent(true);
			close();

			if (showPendingMessage) {
				$isMessageSending(true);
			}
			if (table) {
				table.current.refetchData();
			}
			ReactGA.event({
				category: "Messages",
				action: "SentTemplateMessage",
			});
		} catch (e) {
			console.log("error", e);
			if (batchState.error) {
				addToast(
					"Partial send; check Sent Template Messages for details",
					{
						appearance: "error",
						autoDismiss: true,
					}
				);
			} else {
				addToast("Error sending messages", {
					appearance: "error",
					autoDismiss: true,
				});
			}
			ReactGA.event({
				category: "Messages",
				action: "FailedSendTemplateMessage",
			});
		} finally {
			setDisableSubmit(false);
		}
	};

	const uploadAttachment = async (file) => {
		const { name, type } = file;
		try {
			const token = await getAccessToken();

			const signedUrlResponse = await fetch(
				`${process.env.REACT_APP_API_URL}filessignedurl`,
				{
					method: "POST",
					headers: {
						"Content-Disposition": `filename=${name}`,
						Authorization: `Bearer ${token}`,
						"Content-Type": type,
					},
				}
			);

			const { attachmentId, presignedUrl } =
				await signedUrlResponse.json();

			const attachmentResponse = await fetch(presignedUrl, {
				method: "PUT",
				headers: {
					"Content-Disposition": `filename=${name}`,
					"Content-Type": type,
				},
				body: file,
			});

			if (
				attachmentResponse.status === 200 ||
				attachmentResponse.status === 201
			) {
				return attachmentId;
			} else {
				return false;
			}
		} catch (error) {
			console.log("error", error);
		}
	};

	/**
	 * Determines if we need to render step 2 - if there are no variables or attachments dont render step 2
	 */
	const getSteps = (values) => {
		let steps = [
			{
				title: "Filters",
				component: (
					<Step1
						disabledFields={disabledFields}
						disableSearchBy={disableSearchBy}
						apiAccountIdProp={account}
						setNaturalChatTemplate={setNaturalChatTemplate}
						naturalChatTemplate={naturalChatTemplate}
						setNewNaturalChatTemplate={setNewNaturalChatTemplate}
						newNaturalChatTemplate={newNaturalChatTemplate}
						rootStore={rootStore}
						maxBroadcasts={maxBroadcasts}
						setMaxBroadcasts={setMaxBroadcasts}
						preSubAsSub={preSubAsSub}
						setPreSubAsSub={setPreSubAsSub}
						preSubCount={preSubCount}
						setPreSubCount={setPreSubCount}
						tagsContactsCount={tagsContactsCount}
						setTagsContactsCount={setTagsContactsCount}
						preSubList={preSubList}
						setPreSubList={setPreSubList}
						advancedSaved={advancedSaved}
						setAdvancedSaved={setAdvancedSaved}
						advancedSendContacts={advancedSendContacts}
						setAdvancedSendContacts={setAdvancedSendContacts}
						key="step1"
						setSelectedInbox={setSelectedInbox}
						selectedInbox={selectedInbox}
						selectedTemplate={selectedTemplate}
						setSelectedTemplate={setSelectedTemplate}
					/>
				),
			},
			{
				title: "Variables",
				component: (
					<Step2
						disabledFields={disabledFields}
						selectedInbox={selectedInbox}
						selectedTemplate={selectedTemplate}
					/>
				),
			},
		];
		const hasAttachment =
			values.templateMessages.value?.attachment &&
			values.templateMessages.value?.attachment !== "None";
		const regex = /[^{{\}]+(?=}})/g;
		const textVariables =
			values.templateMessages.value?.text?.match(regex) || [];
		const hasDynamicButtonFields =
			values.buttonUrlParams && values.buttonUrlParams.length > 0;
		if (!hasAttachment && textVariables.length === 0 && !hasDynamicButtonFields) {
			steps.splice(1, 1);
		}

		return steps;
	};

	const handleStepChange = (currentStep) => {
		setCurrentStep(currentStep);
	};

	const renderTitle = () => {
		switch (currentStep) {
			case 1:
				return "Send Template Message";

			case 2:
				return "Variables ";

			default:
				break;
		}
	};

	const renderDescription = () => {
		switch (currentStep) {
			case 1:
				return "Use the form below to send a template message.";

			case 2:
				return "Enter the necessary variable values once you are happy click submit to send.";
			default:
				break;
		}
	};

	return (
		<>
			<ConvertingModal isOpen={converting}></ConvertingModal>

			<MessageSendingProgressModal
				isOpen={batchState.isSending || !!batchState.error}
				currentBatch={batchState.currentBatch}
				totalBatches={batchState.totalBatches}
				errorMessage={batchState.error}
				onClose={() => {
					if (!batchState.isSending) {
						setBatchState({
							isSending: false,
							currentBatch: 0,
							totalBatches: 0,
							error: null,
						});
					}
				}}
			/>

			<Formik
				validationSchema={
					generateValidationScheme(
						disabledFields,
						naturalChatTemplate,
						newNaturalChatTemplate,
						maxBroadcasts,
						preSubAsSub,
						preSubCount,
						tagsContactsCount,
						resellerName,
						advancedSaved
					)[currentStep - 1]
				}
				validateOnBlur={false}
				validateOnChange={false}
				initialValues={{
					total: 0,
					tagFilter: [
						{
							id: 0,
							tagFilter: { label: "Or", value: "Or" },
							selectedTags: [],
						},
					],
					tag: "",
					tags: [],
					contacts: contacts,
					templateMessages: "",
					variables: [],
					buttonUrlParams: [],
					attachments: [],
					templateMessage: "",
					searchBy: "search-contacts",
					account: account,
					message: "",
				}}
				onSubmit={(values, actions) => {
					onSubmit(values, actions);
				}}
			>
				{({ handleSubmit, values, validateForm, resetForm }) => (
					<Modal
						aria={{
							labelledby: "add_template_message_heading",
							describedby:
								"add_template_message_full_description",
						}}
						isOpen={isOpen}
						onRequestClose={() => {
							close();
							resetForm();
						}}
						style={{
							content: {
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
							},
							overlay: {
								backgroundColor: "rgba(0,0,0,0.7)",
							},
						}}
						className="ReactModalScrollable"
					>
						<form
							id="send_template_message"
							onSubmit={handleSubmit}
						>
							<ModalHeader>{renderTitle()}</ModalHeader>
							<ModalDescription>
								{renderDescription()}
							</ModalDescription>
							<ModalContentSeperator />
							<Stepper
								validate={validateForm}
								onStepChange={handleStepChange}
								steps={getSteps(values)}
								closeModal={() => {
									close();
									resetForm();
								}}
								hasMessage={values.message.length > 0}
								disableSubmit={disableSubmit}
							/>
						</form>
					</Modal>
				)}
			</Formik>
		</>
	);
};

SendTemplateMessage.defaultProps = {
	disableSearchBy: false,
	contacts: [],
	disabledFields: {
		searchBy: false,
		contacts: false,
		tags: false,
		templateMessages: false,
		account: false,
	},
	table: null,
};

SendTemplateMessage.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	close: PropTypes.func.isRequired,
	/**
	 * Removes the ability to change the "search by" field. Needed because in the contacts page we prepopulate contacts.
	 */
	disableSearchBy: PropTypes.bool,
	contacts: PropTypes.array,
	disabledFields: PropTypes.object,
};

export default SendTemplateMessage;
